import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import App from './App.jsx';
import { AuthProvider } from './context/AuthContext.jsx';
import ComingSoon from './pages/ComingSoon.jsx';
import InternalGate from './pages/InternalGate.jsx';
import './i18n';   // registers en/es, must come before any t() call
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// Pre-launch gate. When VITE_COMING_SOON=true (set in .env.prod while the
// backend is still being provisioned), render the launch page standalone —
// no Router, no AuthProvider, no API calls. Flip the flag off and redeploy
// when you're ready to expose the real app.
const isComingSoon = import.meta.env.VITE_COMING_SOON === 'true';

/*
 * Two independent gates, and the order matters.
 *
 * InternalGate is OUTERMOST: on a non-public environment nobody should see
 * anything — not even the launch page — until they have proved they are on
 * the internal allow-list. It is a passthrough when VITE_INTERNAL_ONLY is not
 * '1', so public builds are unaffected.
 *
 * ComingSoon then sits inside it, so an internal user who gets past the gate
 * on a pre-launch production still sees the launch page unless that flag is
 * off too. Set VITE_COMING_SOON=false to let internal users through to the
 * real app while the public still meets the launch page at the perimeter.
 */
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <InternalGate>
      {isComingSoon ? (
        <ComingSoon />
      ) : (
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <App />
              <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
            </AuthProvider>
          </QueryClientProvider>
        </BrowserRouter>
      )}
    </InternalGate>
  </React.StrictMode>,
);
